import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  AutocompleteProps
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fromEvent } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import {
  searchNumeroDoc,
  setSelectedDocument
} from "../../reduxStore/documentVenteSlice";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { Document_vente } from "../../models/models";

interface NumeroDocAutoCProps
  extends Partial<AutocompleteProps<Document_vente, false, false, false>> {
  onNumeroDocChange: (doc: Document_vente | null) => void;
  selectedDocument?: Document_vente | null;
}

const NumeroDocAutoC: React.FC<NumeroDocAutoCProps> = ({
  onNumeroDocChange,
  selectedDocument,
  ...autocompleteProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  // État dédié aux suggestions : ne pas lire documentData/loading (tableau de la page)
  const { numeroDocOptions, numeroDocLoading } = useSelector(
    (state: RootState) => state.DocumentVenteSlice
  );
  const [selected, setSelected] = useState<Document_vente | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (selectedDocument) {
      setSelected(selectedDocument);
    }
  }, [selectedDocument]);

  useEffect(() => {
    if (inputRef.current) {
      let pending: { abort: () => void } | null = null;
      const subscription = fromEvent(inputRef.current, "input")
        .pipe(
          debounceTime(500),
          switchMap((event: any) => {
            const numero_doc = event.target.value.trim();
            if (numero_doc.length > 2) {
              // Une frappe plus récente annule la recherche encore en vol
              pending?.abort();
              pending = dispatch(searchNumeroDoc(numero_doc));
            }
            return [];
          })
        )
        .subscribe();

      return () => {
        pending?.abort();
        subscription.unsubscribe();
      };
    }
  }, [dispatch]);

  const handleChange = (_: any, newValue: Document_vente | null) => {
    setSelected(newValue);
    dispatch(setSelectedDocument(newValue));
    onNumeroDocChange(newValue);
  };

  return (
    <Autocomplete
      options={numeroDocOptions}
      value={selected}
      getOptionLabel={(option: Document_vente) => option.numero_doc || ""}
      isOptionEqualToValue={(option, value) => option.numero_doc === value.numero_doc}
      loading={numeroDocLoading}
      onChange={handleChange}
      // onBlur={() => dispatch(clearDocumentData())}
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label="Numéro de document"
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {numeroDocLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};

export default NumeroDocAutoC;
